import React, { useState, useEffect } from "react";
import { Form, Select, message } from "antd";
import ModelWrapper from "../../../../modal/ModelWrapper";
import { groupService } from "../../../../../services/Group";
import { physicianService, ccmCoordinatorService } from "../../../../../services/user";
import styled from "styled-components";
import { CustomButton, CustomSecondaryButton } from "../../../physician_page/styled";

const { Option } = Select;

const AddMemberModal = ({ isOpen, setIsOpen, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [groups, setGroups] = useState([]);
    const [fetchingGroups, setFetchingGroups] = useState(false);
    const [selectedGroupType, setSelectedGroupType] = useState(null);
    const [members, setMembers] = useState([]);
    const [fetchingMembers, setFetchingMembers] = useState(false);

    const fetchGroups = async () => {
        try {
            setFetchingGroups(true);
            const response = await groupService.getAll(100, 1, "");
            const results = response?.data?.hospitals?.results || response?.data?.results || [];
            setGroups(results);
        } catch (error) {
            message.error("Failed to fetch groups");
            setGroups([]);
        } finally {
            setFetchingGroups(false);
        }
    };

    const fetchMembers = async (type) => {
        try {
            setFetchingMembers(true);
            setMembers([]);
            if (type === "clinical") {
                const res = await physicianService.getAll(100, 1, "");
                const results = res?.data?.results || [];
                setMembers(results.map((p) => ({ value: p.id || p._id, label: `${p.name}${p.specialty ? ` : ${p.specialty}` : ""}` })));
            } else if (type === "ccm") {
                const res = await ccmCoordinatorService.getAll(100, 1, "");
                const results = res?.data?.results || [];
                setMembers(results.map((c) => ({ value: c.id || c._id, label: `${c.name} - ${c.email}` })));
            }
        } catch (error) {
            message.error("Failed to fetch members");
        } finally {
            setFetchingMembers(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchGroups();
        } else {
            setSelectedGroupType(null);
            setMembers([]);
        }
    }, [isOpen]);

    const handleGroupChange = (groupId) => {
        const group = groups.find((g) => (g._id || g.id) === groupId);
        const type = group?.type || null;
        setSelectedGroupType(type);
        setMembers([]);
        form.setFieldValue("members", undefined);
        if (type) fetchMembers(type);
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const payload = {
                groupId: values.group,
                memberIds: values.members,
            };
            await groupService.addMemberToGroup(payload);
            message.success("Members added successfully!");
            form.resetFields();
            setSelectedGroupType(null);
            setMembers([]);
            setIsOpen(false);
            if (onSuccess) onSuccess();
        } catch (error) {
            message.error("Failed to add members");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setSelectedGroupType(null);
        setMembers([]);
        setIsOpen(false);
    };

    const memberLabel = selectedGroupType === "ccm" ? "Select Coordinator" : "Select Physician";
    const memberPlaceholder = selectedGroupType === "ccm" ? "Search and select coordinators..." : "Search and select physicians...";

    return (
        <ModelWrapper
            title={
                <div className="row-flex flex-column">
                    <h3 className="font-18 font-semibold text-primary mb-5">
                        Add Members
                    </h3>
                    <p className="font-14 text-secondry mb-0">
                        Select a group then add members to it
                    </p>
                </div>
            }
            footer={null}
            show={isOpen}
            setShow={setIsOpen}
            width={600}
            keyClose={false}
            className="custom-detail-modal"
        >
            <FormContainer>
                <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
                    <Form.Item
                        label="Select Group"
                        name="group"
                        rules={[{ required: true, message: "Please select a group" }]}
                    >
                        <Select
                            placeholder="Select a group..."
                            loading={fetchingGroups}
                            showSearch
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ width: "100%" }}
                            onChange={handleGroupChange}
                        >
                            {groups.map((group) => (
                                <Option
                                    key={group._id || group.id}
                                    value={group._id || group.id}
                                    label={`${group.hospitalName || group.name} - ${(group.type || "").toUpperCase()}`}
                                >
                                    <span>{group.hospitalName || group.name}</span>
                                    {group.type && (
                                        <span style={{ marginLeft: 8, color: group.type === "ccm" ? "#8a3ef4" : "#10b981", fontSize: 12, fontWeight: 600 }}>
                                            — {group.type.toUpperCase()}
                                        </span>
                                    )}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {selectedGroupType && (
                        <Form.Item
                            label={memberLabel}
                            name="members"
                            rules={[{ required: true, message: `Please select at least one member` }]}
                        >
                            <Select
                                mode="multiple"
                                placeholder={memberPlaceholder}
                                loading={fetchingMembers}
                                showSearch
                                filterOption={(input, option) =>
                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                }
                                style={{ width: "100%" }}
                                options={members}
                            />
                        </Form.Item>
                    )}

                    <div className="row-flex justify-end">
                        <CustomSecondaryButton
                            type="default"
                            onClick={handleCancel}
                            disabled={loading}
                            style={{ marginRight: "12px" }}
                        >
                            Cancel
                        </CustomSecondaryButton>
                        <CustomButton
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                        >
                            Add Members
                        </CustomButton>
                    </div>
                </Form>
            </FormContainer>
        </ModelWrapper>
    );
};

export default AddMemberModal;

const FormContainer = styled.div`
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;

    .ant-form-item-label > label {
        font-weight: 500;
        color: var(--primary-text-color);
    }

    .ant-select-selector {
        border-radius: 6px;
        transition: all 0.3s;
        
        &:hover {
            border-color: rgba(138, 62, 244, 0.5);
        }
        
        &:focus, &.ant-select-focused .ant-select-selector {
            border-color: rgba(138, 62, 244, 0.7);
            box-shadow: 0 0 0 2px rgba(138, 62, 244, 0.1);
        }
    }

    .ant-select-multiple .ant-select-selection-item {
        background-color: rgba(138, 62, 244, 0.1);
        border-color: rgba(138, 62, 244, 0.3);
        color: var(--primary-color);
    }

    /* Custom option hover effect */
    .physician-option:hover {
        background: #f5f5fa;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid var(--border-color);
`;